<template>
    <div class="space-y-6">
        <!-- Page Header -->
        <div class="text-center mb-8">
            <!-- Icon -->
            <div class="mx-auto w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mb-4">
                <svg class="w-8 h-8 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
            </div>

            <h1 class="text-3xl font-bold text-secondary-900 dark:text-white mb-2">
                Verify Your Email
            </h1>
            <p class="text-secondary-600 dark:text-secondary-400">
                We've sent a verification link to your email address
            </p>
        </div>

        <!-- Email Address Display -->
        <div class="bg-secondary-50 dark:bg-secondary-900/50 rounded-lg p-4 mb-6">
            <div class="flex items-center space-x-3">
                <svg class="w-5 h-5 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                </svg>
                <span class="text-secondary-700 dark:text-secondary-300 font-medium">
                    {{ userEmail || 'your email address' }}
                </span>
            </div>
        </div>

        <!-- Success Message (after resend) -->
        <div
            v-if="verificationLinkSent"
            class="mb-6 p-4 bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 rounded-lg"
        >
            <div class="flex items-start space-x-3">
                <svg class="w-5 h-5 text-success-600 dark:text-success-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                </svg>
                <div>
                    <p class="text-sm text-success-800 dark:text-success-200 font-medium">
                        Verification link sent successfully!
                    </p>
                    <p class="text-sm text-success-700 dark:text-success-300 mt-1">
                        Please check your email inbox and spam folder.
                    </p>
                </div>
            </div>
        </div>

        <!-- Error Message -->
        <div
            v-if="form.errors.email"
            class="mb-6 p-4 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg"
        >
            <div class="flex items-start space-x-3">
                <svg class="w-5 h-5 text-danger-600 dark:text-danger-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                </svg>
                <p class="text-sm text-danger-800 dark:text-danger-200">
                    {{ form.errors.email }}
                </p>
            </div>
        </div>

        <!-- TEMPORARY: Testing Verification URL - REMOVE IN PRODUCTION -->
        <div
            v-if="verificationUrl"
            class="mb-6 p-4 bg-warning-50 dark:bg-warning-900/20 border-2 border-warning-400 dark:border-warning-600 rounded-lg"
        >
            <div class="flex items-start space-x-3 mb-3">
                <svg class="w-5 h-5 text-warning-600 dark:text-warning-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                </svg>
                <div class="flex-1">
                    <p class="text-sm text-warning-800 dark:text-warning-200 font-bold mb-1">
                        TESTING MODE - Verification Link
                    </p>
                    <p class="text-xs text-warning-700 dark:text-warning-300 mb-2">
                        This is visible because you're using dummy emails. Click the link below to verify:
                    </p>
                    <a
                        :href="verificationUrl"
                        class="inline-flex items-center px-3 py-2 bg-warning-600 hover:bg-warning-700 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Click Here to Verify Email
                    </a>
                    <div class="mt-3 p-2 bg-warning-100 dark:bg-warning-900/40 rounded text-xs text-warning-900 dark:text-warning-100 font-mono break-all">
                        {{ verificationUrl }}
                    </div>
                </div>
            </div>
        </div>

        <!-- Instructions -->
        <div class="mb-8 space-y-4">
            <div class="flex items-start space-x-3">
                <div class="flex-shrink-0 w-6 h-6 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center text-xs font-bold text-primary-600 dark:text-primary-400">
                    1
                </div>
                <p class="text-sm text-secondary-700 dark:text-secondary-300 pt-0.5">
                    Check your email inbox for a message from us
                </p>
            </div>

            <div class="flex items-start space-x-3">
                <div class="flex-shrink-0 w-6 h-6 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center text-xs font-bold text-primary-600 dark:text-primary-400">
                    2
                </div>
                <p class="text-sm text-secondary-700 dark:text-secondary-300 pt-0.5">
                    Click the verification link in the email
                </p>
            </div>

            <div class="flex items-start space-x-3">
                <div class="flex-shrink-0 w-6 h-6 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center text-xs font-bold text-primary-600 dark:text-primary-400">
                    3
                </div>
                <p class="text-sm text-secondary-700 dark:text-secondary-300 pt-0.5">
                    You'll be automatically redirected to your dashboard
                </p>
            </div>
        </div>

        <!-- Resend Button -->
        <div class="mb-6">
            <button
                @click="resendVerification"
                :disabled="form.processing || cooldownActive"
                type="button"
                class="w-full btn btn-primary cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <svg
                    v-if="form.processing"
                    class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span v-if="form.processing">Sending...</span>
                <span v-else-if="cooldownActive">Resend in {{ cooldownSeconds }}s</span>
                <span v-else>Resend Verification Email</span>
            </button>

            <p class="text-xs text-center text-secondary-500 dark:text-secondary-500 mt-2">
                Didn't receive the email? Check your spam folder or click to resend
            </p>
        </div>

        <!-- Divider -->
        <div class="relative mb-6">
            <div class="absolute inset-0 flex items-center">
                <div class="w-full border-t border-secondary-200 dark:border-secondary-700"></div>
            </div>
            <div class="relative flex justify-center text-sm">
                <span class="px-2 bg-white dark:bg-secondary-800 text-secondary-500 dark:text-secondary-400">
                    or
                </span>
            </div>
        </div>

        <!-- Alternative Actions -->
        <div class="space-y-3">
            <!-- Back to Login -->
            <Link
                href="/login"
                class="block w-full text-center px-4 py-2.5 border border-secondary-300 dark:border-secondary-600 rounded-lg text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-800 transition-colors font-medium"
            >
                Back to Login
            </Link>

            <!-- Logout -->
            <button
                @click="logout"
                type="button"
                class="block w-full text-center px-4 py-2.5 text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-secondary-200 transition-colors font-medium"
            >
                Logout
            </button>
        </div>

        <!-- Help Text -->
        <div class="mt-8 p-4 bg-info-50 dark:bg-info-900/20 border border-info-200 dark:border-info-800 rounded-lg">
            <div class="flex items-start space-x-3">
                <svg class="w-5 h-5 text-info-600 dark:text-info-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                </svg>
                <div class="text-sm">
                    <p class="text-info-800 dark:text-info-200 font-medium mb-1">
                        Having trouble?
                    </p>
                    <p class="text-info-700 dark:text-info-300">
                        If you continue to have issues, please contact our support team at
                        <a href="mailto:support@marginresolve.com" class="underline hover:text-info-900 dark:hover:text-info-100">
                            support@marginresolve.com
                        </a>
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { Link, useForm, router, usePage } from '@inertiajs/vue3';

// Props
const props = defineProps({
    verificationLinkSent: {
        type: Boolean,
        default: false,
    },
    email: {
        type: String,
        default: '',
    },
    verificationUrl: {
        type: String,
        default: null,
    },
});

// Page props
const page = usePage();
const user = computed(() => page.props.auth?.user || null);
const userEmail = computed(() => user.value?.email || props.email || '');

// Form
const form = useForm({});

// Cooldown state (prevent spam)
const cooldownActive = ref(false);
const cooldownSeconds = ref(60);
let cooldownInterval = null;

// Resend verification email
const resendVerification = () => {
    if (cooldownActive.value) return;

    // Create form with user's email
    const resendForm = useForm({
        email: userEmail.value,
    });

    resendForm.post('/api/auth/resend-verification', {
        preserveScroll: true,
        onSuccess: () => {
            // Start cooldown timer
            startCooldown();
        },
        onError: (errors) => {
            // Copy errors to main form for display
            form.errors = resendForm.errors;
        },
    });
};

// Start cooldown timer
const startCooldown = () => {
    cooldownActive.value = true;
    cooldownSeconds.value = 60;

    cooldownInterval = setInterval(() => {
        cooldownSeconds.value--;

        if (cooldownSeconds.value <= 0) {
            stopCooldown();
        }
    }, 1000);
};

// Stop cooldown timer
const stopCooldown = () => {
    cooldownActive.value = false;
    cooldownSeconds.value = 60;
    if (cooldownInterval) {
        clearInterval(cooldownInterval);
        cooldownInterval = null;
    }
};

// Logout
const logout = () => {
    router.post('/api/auth/logout', {}, {
        onSuccess: () => {
            router.visit('/login');
        },
    });
};

// Check if link was just sent from registration
onMounted(() => {
    // If user just registered, start cooldown to prevent immediate resend
    if (props.verificationLinkSent) {
        startCooldown();
    }
});

// Clean up interval on unmount
onUnmounted(() => {
    stopCooldown();
});
</script>

<style scoped>
/* Smooth transitions */
* {
    transition-property: color, background-color, border-color, opacity;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 150ms;
}
</style>
